<template>
  <div class="flex flex-wrap">
    <div class="w-full mb-6 px-4" v-if="displayMisHoras">
      <div
        class="relative flex flex-col min-w-0 break-words w-full shadow-lg rounded bg-white mb-6"
      >
        <card-month-nav subtitle="Mis horas" />
      </div>
      <card-bar-chart
        :records="horasPorSchedulerChartRecords"
        label="fecha"
        value="horas"
        datasetName="Horas"
      />
    </div>
    <div class="w-full mb-12 px-4">
      <card-list
        title="Horas"
        :resource="resource"
        primaryKey="idhora"
        :records="records"
        :meta="meta"
        :columns="filteredColumns"
      >
        <template v-slot:actions v-if="puedeExportar">
          <button
            class="bg-yellow-500 text-white font-bold uppercase text-xs px-4 py-2 rounded shadow hover:shadow-md outline-none focus:outline-none mr-1 ease-linear transition-all duration-150"
            type="button"
            @click="exportarCSV()"
          >
            {{ $t("Exportar CSV") }}
          </button>
        </template>
      </card-list>
    </div>
  </div>
</template>
<script>
import { mapState } from "vuex";
import { useToast } from "vue-toastification";
import CardList from "@/components/Cards/CardList.vue";
import CardMonthNav from "@/components/Cards/CardMonthNav.vue";
import CardBarChart from "@/components/Cards/CardBarChart.vue";
import HoraService from "@/services/hora.service";
import ReporteService from "@/services/reporte.service";
import { hora } from "@/models/hora";
import DateUtils from "@/utils/dateUtils";
import { hasActiveFilters } from "@/utils/filterUtils";
import { descargarCSV } from "@/utils/descargas";

export default {
  components: {
    CardList,
    CardMonthNav,
    CardBarChart,
  },

  data() {
    return {
      records: null,
      meta: {},
      columns: hora.filter((column) => column.showOnList),
      horasPorScheduler: [],
    };
  },

  computed: {
    ...mapState({
      userInfo: (state) => state.auth.user,
      startDateFromState: (state) => state.filters.startDate,
      endDateFromState: (state) => state.filters.endDate,
    }),

    currentRoutePath() {
      return this.$route.path;
    },

    displayMisHoras() {
      if (!["/mis-horas"].includes(this.currentRoutePath)) {
        return false;
      }
      const roles = this.userInfo?.roles || [];
      return (
        roles.includes("personal") ||
        roles.includes("coordinador") ||
        roles.includes("supervisor de empresa")
      );
    },

    resource() {
      return this.displayMisHoras ? "mis-horas" : "horas";
    },

    puedeExportar() {
      const roles = this.userInfo?.roles || [];
      return roles.includes("supervisor de empresa") || roles.includes("admin");
    },

    filteredColumns() {
      return this.columns.filter((column) => {
        if (this.displayMisHoras) {
          return column.name !== "idpersonal";
        }
        return true;
      });
    },

    horasPorSchedulerChartRecords() {
      const groupedRecords = this.horasPorScheduler.reduce((acc, obj) => {
        if (!acc[obj.fecha]) {
          acc[obj.fecha] = 0;
        }
        acc[obj.fecha] += parseFloat(obj.horas);
        return acc;
      }, {});

      const records = Object.keys(groupedRecords).map((fecha) => ({
        fecha: parseInt(fecha.split("-")[2]),
        horas: groupedRecords[fecha],
      }));

      return records;
    },

    queryParams() {
      const params = { ...this.$route.query };
      delete params.startDate;
      delete params.endDate;
      if (this.startDateFromState && this.endDateFromState) {
        params.startDate = this.startDateFromState;
        params.endDate = this.endDateFromState;
      }
      return params;
    },
  },

  watch: {
    $route: {
      immediate: true,
      handler(/*to, from*/) {
        this.loadRecords();

        ["startDateFromState", "endDateFromState"].forEach((property) => {
          this.$watch(
            () => this[property],
            () => {
              const queryParams = new URLSearchParams();
              queryParams.set("page", 1);
              if (this.startDateFromState && this.endDateFromState) {
                queryParams.set("startDate", this.startDateFromState);
                queryParams.set("endDate", this.endDateFromState);
              }
              const queryString = queryParams.toString();
              const url = `/${this.resource}?${queryString}`;
              this.$router.push(url);
            }
          );
        });
      },
    },
  },

  methods: {
    loadRecords() {
      // undefined = FilterStats aún no inicializó; null = histórico (sin filtro de fechas)
      if (
        this.startDateFromState === undefined ||
        this.endDateFromState === undefined
      ) {
        this.records = [];
        this.meta = {};
        this.horasPorScheduler = [];
        return;
      }

      HoraService.list({ ...this.queryParams, personal: this.displayMisHoras })
        .then((data) => {
          this.records = data.records;
          this.meta = data.meta;
        })
        .catch((response) => {
          console.log(response);
        });

      if (this.displayMisHoras) {
        // El gráfico acompaña al listado: se le pasa su mismo filtro
        ReporteService.horasPorScheduler({
          startDate: this.startDateFromState,
          endDate: this.endDateFromState,
          filters: this.$route.query.filters,
        })
          .then((data) => {
            this.horasPorScheduler = data.horas;
          })
          .catch((response) => {
            console.log(response);
          });
      }
    },

    exportarCSV() {
      if (
        hasActiveFilters(this.$route.query) &&
        !confirm(this.$t("confirm.exportSelection"))
      ) {
        return;
      }

      const toast = useToast();
      // Usar los mismos parámetros que la lista actual, incluyendo todos los filtros
      const params = { ...this.queryParams };

      HoraService.exportarCSV(params)
        .then((data) => {
          if (data.csv) {
            const fileName = `Horas_${DateUtils.formatDate(new Date())}.csv`;
            descargarCSV(data.csv, fileName);
            toast.success("CSV exportado exitosamente");
          } else {
            toast.warning("No hay datos para exportar");
          }
        })
        .catch((error) => {
          toast.error("Error al exportar CSV");
          console.error(error);
        });
    },
  },
};
</script>
